import type { Key } from "react-aria-components";

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFilter } from "react-aria";
import { Button, ComboBox } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { Description, Input, Label } from "../field";
import { ChevronDownIcon } from "../icons";
import { ListBox, ListBoxItem } from "../list-box";
import { Popover } from "../popover";
import { Tag, TagGroup, TagList } from "../tag-group";
import { inputFieldStyle } from "../utils";

const SPLIT_REGEX = /[\n,]/;

export function splitTags(value?: null | string): string[] {
  if (!value) {
    return [];
  }

  return value
    .split(SPLIT_REGEX)
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);
}

export function joinTags(tags: string[]): string {
  return tags.join(", ");
}

export function ReadOnlyTags({
  emptyLabel = "Not specified",
  value,
}: {
  emptyLabel?: string;
  value?: null | string;
}) {
  const tags = splitTags(value);

  if (tags.length === 0) {
    return <>{emptyLabel}</>;
  }

  return (
    <TagGroup className="contents">
      <TagList items={tags.map((tag) => ({ id: tag }))}>
        {(item) => <Tag textValue={String(item.id)}>{item.id}</Tag>}
      </TagList>
    </TagGroup>
  );
}

export interface TagInputProps {
  className?: string;
  description?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  label: string;
  name?: string;
  onBlur?: () => void;
  onChange: (tags: string[]) => void;
  placeholder?: string;
  requiredHint?: boolean;
  /** When provided, renders a "creatable" combobox: suggestions dropdown + free text. */
  suggestions?: string[];
  value: string[];
}

const controlBoxStyle = [
  "relative bg-bg-primary",
  "flex min-h-9 w-full flex-row flex-wrap items-center rounded-md",
  "border border-border-primary has-[input[data-focused=true]]:border-border-brand",
  "has-[input[data-invalid=true][data-focused=true]]:border-border-brand has-[input[data-invalid=true]]:border-red-400",
  "has-[input[data-focused=true]]:ring-4 has-[input[data-focused=true]]:ring-border-brand/25",
  "px-[14px] py-[10px] gap-[4px]",
];

function useTagActions(value: string[], onChange: (tags: string[]) => void) {
  const addTags = useCallback(
    (raw: string) => {
      const additions = splitTags(raw);

      if (additions.length === 0) {
        return;
      }

      const next = [...value];

      for (const tag of additions) {
        if (!next.includes(tag)) {
          next.push(tag);
        }
      }

      if (next.length !== value.length) {
        onChange(next);
      }
    },
    [onChange, value],
  );

  const removeTags = useCallback(
    (keys: Set<Key>) => {
      onChange(value.filter((tag) => !keys.has(tag)));
    },
    [onChange, value],
  );

  const removeLast = useCallback(() => {
    if (value.length > 0) {
      onChange(value.slice(0, -1));
    }
  }, [onChange, value]);

  return { addTags, removeLast, removeTags };
}

function TagPills({
  isDisabled,
  onRemove,
  value,
}: {
  isDisabled?: boolean;
  onRemove: (keys: Set<Key>) => void;
  value: string[];
}) {
  if (value.length === 0) {
    return null;
  }

  return (
    <TagGroup className="contents" onRemove={isDisabled ? undefined : onRemove}>
      <TagList
        className="outline-hidden"
        items={value.map((tag) => ({ id: tag }))}
      >
        {(item) => <Tag textValue={String(item.id)}>{item.id}</Tag>}
      </TagList>
    </TagGroup>
  );
}

function TagFieldShell({
  children,
  className,
  controlRef,
  description,
  errorMessage,
  inputId,
  isDisabled,
  isInvalid,
  label,
  requiredHint,
}: {
  children: React.ReactNode;
  controlRef?: React.Ref<HTMLDivElement>;
  inputId: string;
} & Pick<
  TagInputProps,
  | "className"
  | "description"
  | "errorMessage"
  | "isDisabled"
  | "isInvalid"
  | "label"
  | "requiredHint"
>) {
  return (
    <div className={twMerge(inputFieldStyle, "min-w-0", className)}>
      <Label htmlFor={inputId} requiredHint={requiredHint}>
        {label}
      </Label>
      <div
        className={twMerge(
          controlBoxStyle,
          isDisabled && "bg-bg-disabled-subtle",
        )}
        data-ui="control"
        ref={controlRef}
      >
        {children}
      </div>
      {description ? <Description>{description}</Description> : null}
      {isInvalid && errorMessage ? (
        <span
          className="block text-sm font-normal text-text-error-primary"
          slot="errorMessage"
        >
          {errorMessage}
        </span>
      ) : null}
    </div>
  );
}

function FreeTextTagInput({
  className,
  description,
  errorMessage,
  isDisabled,
  isInvalid,
  label,
  name,
  onBlur,
  onChange,
  placeholder,
  requiredHint,
  value,
}: TagInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const inputId = useId();
  const { addTags, removeLast, removeTags } = useTagActions(value, onChange);

  const commit = useCallback(() => {
    if (inputValue.trim().length > 0) {
      addTags(inputValue);
    }

    setInputValue("");
  }, [addTags, inputValue]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "," || event.key === " ") {
      if (event.key === " " && inputValue.trim().length === 0) {
        return;
      }

      event.preventDefault();
      commit();

      return;
    }

    if (
      event.key === "Backspace" &&
      inputValue.length === 0 &&
      value.length > 0
    ) {
      event.preventDefault();
      removeLast();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text");

    if (SPLIT_REGEX.test(text)) {
      event.preventDefault();
      addTags(text);
      setInputValue("");
    }
  };

  return (
    <TagFieldShell
      className={className}
      description={description}
      errorMessage={errorMessage}
      inputId={inputId}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      label={label}
      requiredHint={requiredHint}
    >
      <TagPills isDisabled={isDisabled} onRemove={removeTags} value={value} />
      <input
        aria-invalid={isInvalid || undefined}
        className="min-w-3xl flex-1 border-0 bg-transparent px-0.5 py-0 text-md text-text-primary outline-hidden placeholder:text-text-placeholder"
        data-focused={isFocused ? "true" : undefined}
        data-invalid={isInvalid ? "true" : undefined}
        disabled={isDisabled}
        id={inputId}
        onBlur={() => {
          commit();
          setIsFocused(false);
          onBlur?.();
        }}
        onChange={(event) => setInputValue(event.target.value)}
        onFocus={() => setIsFocused(true)}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={value.length === 0 ? placeholder : undefined}
        value={inputValue}
      />
      {name ? (
        <input hidden name={name} readOnly value={joinTags(value)} />
      ) : null}
    </TagFieldShell>
  );
}

function CreatableTagSelect({
  className,
  description,
  errorMessage,
  isDisabled,
  isInvalid,
  label,
  name,
  onBlur,
  onChange,
  placeholder,
  requiredHint,
  suggestions,
  value,
}: { suggestions: string[] } & TagInputProps) {
  const { contains } = useFilter({ sensitivity: "base" });
  const [inputValue, setInputValue] = useState("");
  const inputId = useId();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const { addTags, removeLast, removeTags } = useTagActions(value, onChange);

  useEffect(() => {
    const trigger = triggerRef.current;

    if (!trigger) {
      return;
    }

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setWidth(entry.target.clientWidth);
      }
    });

    observer.observe(trigger);

    return () => observer.disconnect();
  }, []);

  const available = useMemo(
    () =>
      suggestions
        .filter((item) => !value.includes(item) && contains(item, inputValue))
        .map((item) => ({ id: item })),
    [suggestions, value, inputValue, contains],
  );

  const commit = () => {
    if (inputValue.trim().length > 0) {
      addTags(inputValue);
    }

    setInputValue("");
  };

  const onKeyDownCapture = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "," || event.key === " ") {
      // Nothing typed: let the ComboBox handle the key (e.g. select a
      // keyboard-highlighted suggestion). With text, create a tag from it.
      if (inputValue.trim().length === 0) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      commit();

      return;
    }

    if (
      event.key === "Backspace" &&
      inputValue.length === 0 &&
      value.length > 0
    ) {
      event.preventDefault();
      removeLast();
    }
  };

  return (
    <TagFieldShell
      className={className}
      controlRef={triggerRef}
      description={description}
      errorMessage={errorMessage}
      inputId={inputId}
      isDisabled={isDisabled}
      isInvalid={isInvalid}
      label={label}
      requiredHint={requiredHint}
    >
      <TagPills isDisabled={isDisabled} onRemove={removeTags} value={value} />
      <ComboBox
        allowsCustomValue
        allowsEmptyCollection
        aria-label={label}
        className="flex min-w-0 flex-1 items-center gap-[4px]"
        inputValue={inputValue}
        isDisabled={isDisabled}
        isInvalid={isInvalid}
        items={available}
        menuTrigger="focus"
        onInputChange={setInputValue}
        onSelectionChange={(key) => {
          if (key != null) {
            addTags(String(key));
            setInputValue("");
          }
        }}
        selectedKey={null}
      >
        <Input
          className="min-w-3xl flex-1 border-0 bg-transparent px-0.5 py-0 text-md text-text-primary outline-hidden placeholder:text-text-placeholder"
          id={inputId}
          onBlur={() => {
            commit();
            onBlur?.();
          }}
          onKeyDownCapture={onKeyDownCapture}
          placeholder={value.length === 0 ? placeholder : undefined}
        />
        <Button
          aria-label="Show suggestions"
          className="flex size-5 shrink-0 cursor-pointer items-center justify-center text-fg-quinary outline-hidden"
        >
          <ChevronDownIcon className="size-5" />
        </Button>
        <Popover
          offset={4}
          style={{ width: `${width}px` }}
          triggerRef={triggerRef}
        >
          <ListBox
            className="flex max-h-[inherit] flex-col gap-0.5 overflow-auto p-1.5 outline-hidden"
            renderEmptyState={() => (
              <span className="block p-2 text-sm text-text-tertiary">
                {inputValue
                  ? `Press enter to add "${inputValue}"`
                  : "No suggestions"}
              </span>
            )}
          >
            {(item: { id: string }) => (
              <ListBoxItem
                className="cursor-default rounded-md px-md py-[10px] text-md text-text-primary data-[focused=true]:bg-bg-primary-hover"
                id={item.id}
                textValue={item.id}
              >
                {item.id}
              </ListBoxItem>
            )}
          </ListBox>
        </Popover>
      </ComboBox>
      {name ? (
        <input hidden name={name} readOnly value={joinTags(value)} />
      ) : null}
    </TagFieldShell>
  );
}

export function TagInput(props: TagInputProps) {
  if (props.suggestions && props.suggestions.length > 0) {
    return <CreatableTagSelect {...props} suggestions={props.suggestions} />;
  }

  return <FreeTextTagInput {...props} />;
}
